import { useState, useEffect, useCallback } from 'react';
import { serviceUserService } from '../services/serviceUserService';
import { ServiceUser, ServiceUserFilters, ServiceUserStats } from '../models/ServiceUser';

interface UseServiceUsersOptions {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'all' | 'active' | 'inactive' | 'suspended' | 'pending';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

interface UseServiceUsersReturn {
  serviceUsers: ServiceUser[];
  loading: boolean;
  error: string | null;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasNext: boolean;
    hasPrev: boolean;
    limit: number;
  } | null;
  stats: ServiceUserStats | null;
  refreshData: () => Promise<void>;
  updateFilters: (filters: Partial<ServiceUserFilters>) => void;
}

export const useServiceUsers = (options: UseServiceUsersOptions = {}): UseServiceUsersReturn => {
  const [serviceUsers, setServiceUsers] = useState<ServiceUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<UseServiceUsersReturn['pagination']>(null);
  const [stats, setStats] = useState<ServiceUserStats | null>(null);
  const [filters, setFilters] = useState<ServiceUserFilters>({
    page: options.page || 1,
    limit: options.limit || 10,
    search: options.search,
    status: options.status || 'all',
    sortBy: options.sortBy,
    sortOrder: options.sortOrder,
  });

  const fetchServiceUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await serviceUserService.getServiceUsers(filters);
      
      if (response.success) {
        setServiceUsers(response.data.serviceUsers);
        setPagination(response.data.pagination);
      } else {
        throw new Error(response.message || 'Failed to fetch service users');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch service users';
      console.error('❌ Error fetching service users:', errorMessage);
      setError(errorMessage);
      setServiceUsers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await serviceUserService.getServiceUserStats();
      setStats(statsData);
    } catch (err) {
      console.error('❌ Error fetching service user stats:', err);
      // Don't set error for stats failure, just log it
    }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchServiceUsers(), fetchStats()]);
  }, [fetchServiceUsers, fetchStats]);

  const updateFilters = useCallback((newFilters: Partial<ServiceUserFilters>) => {
    setFilters(prev => {
      const updated = {
        ...prev,
        ...newFilters,
      };
      return updated;
    });
  }, []);

  // Initial data fetch
  useEffect(() => {
    refreshData();
  }, [refreshData]);

  // Update filters when options change
  useEffect(() => {
    const newFilters = {
      page: options.page || 1,
      limit: options.limit || 10,
      search: options.search !== undefined ? options.search : undefined,
      status: options.status || 'all',
      sortBy: options.sortBy !== undefined ? options.sortBy : undefined,
      sortOrder: options.sortOrder || undefined,
    };
    
    
    setFilters(newFilters);
  }, [options.page, options.limit, options.search, options.status, options.sortBy, options.sortOrder]);

  return {
    serviceUsers,
    loading,
    error,
    pagination,
    stats,
    refreshData,
    updateFilters,
  };
};
